/**
 * List health, on the Emails overview: what AWS was promised and whether it
 * is holding. Hard-bounce and complaint rates over the last 30 days against
 * Amazon's review thresholds (bounces under 5%, complaints under 0.1%),
 * with amber well before red; what address verification found; credits
 * left; and the never-email list by reason.
 *
 * Always the last 30 days, whatever range the page above shows, because
 * that is the window Amazon looks at.
 */
import { Alert, Anchor, Badge, Box, Card, Group, Loader, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useEmailHygiene, type EmailHygiene } from '../../api/queries';
import { TOKENS, formatNumber } from '../../theme';
import { REASONS, statusLabel } from './reasons';

const HEALTHY = { color: TOKENS.greenText, label: 'Healthy' };
const LEVEL: Record<string, { color: string; label: string }> = {
  ok: HEALTHY,
  amber: { color: TOKENS.amberText, label: 'Watch' },
  red: { color: TOKENS.redText, label: 'Over the limit' },
};

function percent(v: number, digits = 2): string {
  return `${(v * 100).toFixed(digits)}%`;
}

/** A rate on a track from 0 to twice the red line, with the amber and red marks drawn on it. */
export function RateMeter({
  label, rate, amber, red, level, hint, testId,
}: { label: string; rate: number; amber: number; red: number; level: string; hint: string; testId: string }) {
  const max = red * 2;
  const at = (v: number) => `${Math.min(100, (v / max) * 100)}%`;
  const l = LEVEL[level] ?? HEALTHY;
  return (
    <Stack gap={4} data-testid={testId} data-level={level}>
      <Group justify="space-between" wrap="nowrap">
        <Group gap={4} wrap="nowrap">
          <Text size="sm">{label}</Text>
          <Tooltip label={hint} multiline w={260} withArrow><IconInfoCircle size={14} color={TOKENS.textMuted} /></Tooltip>
        </Group>
        <Group gap={6} wrap="nowrap">
          <Text size="sm" fw={700} style={{ color: l.color }}>{percent(rate)}</Text>
          <Badge size="xs" variant="light" color={level === 'red' ? 'red' : level === 'amber' ? 'chartAmber' : 'teal'}>{l.label}</Badge>
        </Group>
      </Group>
      <Box pos="relative" h={8} style={{ background: TOKENS.surface2, borderRadius: 4 }} aria-hidden>
        <Box pos="absolute" left={0} top={0} h={8} w={at(rate)} style={{ background: l.color, borderRadius: 4 }} />
        <Box pos="absolute" top={-3} h={14} w={2} left={at(amber)} style={{ background: TOKENS.amberText }} />
        <Box pos="absolute" top={-3} h={14} w={2} left={at(red)} style={{ background: TOKENS.redText }} />
      </Box>
      <Group justify="space-between">
        <Text size="xs" c="dimmed">Amber from {percent(amber, amber < 0.01 ? 2 : 1)}</Text>
        <Text size="xs" c="dimmed">Amazon reviews above {percent(red, red < 0.01 ? 1 : 0)}</Text>
      </Group>
    </Stack>
  );
}

function Verification({ h }: { h: EmailHygiene }) {
  const total = h.verified_30d || 1;
  const groups = [
    { key: 'send', label: 'Will be sent', color: TOKENS.greenText },
    { key: 'unverified', label: 'Held back (unclear)', color: TOKENS.amberText },
    { key: 'invalid', label: 'Never sent (invalid)', color: TOKENS.redText },
  ].map((g) => ({ ...g, n: h.verification.filter((v) => v.decision === g.key).reduce((a, v) => a + v.count, 0) }));
  return (
    <Stack gap={8} data-testid="verification-breakdown">
      <Group justify="space-between">
        <Text size="sm" fw={600}>Addresses checked, last 30 days</Text>
        <Text size="sm" fw={600}>{formatNumber(h.verified_30d)}</Text>
      </Group>
      <Group gap={0} h={10} style={{ borderRadius: 5, overflow: 'hidden', background: TOKENS.surface2 }} wrap="nowrap">
        {groups.map((g) => (
          <Box key={g.key} h={10} w={`${(g.n / total) * 100}%`} style={{ background: g.color }} />
        ))}
      </Group>
      <Group gap="md">
        {groups.map((g) => (
          <Group key={g.key} gap={6}>
            <Box w={10} h={10} style={{ background: g.color, borderRadius: 2 }} />
            <Text size="xs" c="dimmed">{g.label}: {formatNumber(g.n)}</Text>
          </Group>
        ))}
      </Group>
      <Stack gap={2}>
        {h.verification.slice(0, 8).map((v) => (
          <Group key={v.status} justify="space-between">
            <Text size="xs">{statusLabel(v.status)}</Text>
            <Text size="xs" c="dimmed">{formatNumber(v.count)}</Text>
          </Group>
        ))}
      </Stack>
      {(h.skipped_invalid_30d > 0 || h.skipped_unverified_30d > 0) && (
        <Text size="xs" c="dimmed">
          Emails not sent because of this: {formatNumber(h.skipped_invalid_30d)} invalid,{' '}
          {formatNumber(h.skipped_unverified_30d)} unverified.
        </Text>
      )}
    </Stack>
  );
}

export function ListHealth() {
  const q = useEmailHygiene();
  const h = q.data;
  if (q.isPending) return <Group justify="center" py="md"><Loader size="sm" /></Group>;
  if (q.error) return <Alert color="red" variant="light">{q.error.message}</Alert>;
  if (!h) return null;
  const s = h.suppressions;
  return (
    <Card withBorder radius="md" padding="md" data-testid="list-health">
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" fw={600}>List health, last 30 days</Text>
          <Text size="xs" c="dimmed">{formatNumber(h.sent_30d)} marketing emails sent</Text>
        </Group>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          <Stack gap="md">
            <RateMeter testId="bounce-meter" label="Hard bounces" rate={h.bounce_rate} amber={h.thresholds.bounce_amber}
              red={h.thresholds.bounce_red} level={h.bounce_level}
              hint={`Permanent bounces only, which is what Amazon counts. ${formatNumber(h.hard_bounces_30d)} hard, ${formatNumber(h.soft_bounces_30d)} soft (mailbox full and the like, not held against you).`} />
            <RateMeter testId="complaint-meter" label="Spam complaints" rate={h.complaint_rate} amber={h.thresholds.complaint_amber}
              red={h.thresholds.complaint_red} level={h.complaint_level}
              hint={`${formatNumber(h.complaints_30d)} complaints. Everyone who complains goes on the never-email list.`} />
          </Stack>
          <Verification h={h} />
          <Stack gap={8}>
            <Group justify="space-between">
              <Text size="sm" fw={600}>Verification credits</Text>
              {h.credits ? (
                <Text size="sm" fw={700} data-testid="credits-left">{formatNumber(h.credits.total)}</Text>
              ) : (
                <Badge variant="light" color="gray">{h.elv_configured ? 'Unknown' : 'Free checks only'}</Badge>
              )}
            </Group>
            {h.credits_error && <Text size="xs" c="red">{h.credits_error}</Text>}
            {!h.elv_configured && (
              <Text size="xs" c="dimmed">
                Add an EmailListVerify key in <Anchor component={Link} to="/settings?tab=organisation" size="xs">Settings, under Services</Anchor> to check mailboxes as well as domains.
              </Text>
            )}
            <Text size="sm" fw={600} mt="xs">Never email, by reason</Text>
            {(['unsubscribe', 'bounce', 'complaint', 'invalid', 'sunset', 'manual'] as const).map((k) => (
              <Group key={k} justify="space-between">
                <Text size="xs">{REASONS[k]?.label ?? k}</Text>
                <Text size="xs" fw={600}>{formatNumber(s[k])}</Text>
              </Group>
            ))}
            <Text size="xs" c="dimmed">{formatNumber(h.sunset_7d)} sunset in the last 7 days.</Text>
          </Stack>
        </SimpleGrid>
      </Stack>
    </Card>
  );
}
